import { OpportunityPermissionsService } from '@app/opportunities/services/opportunity-permissions.service';
import { Injectable } from '@angular/core';
import { of, Observable, zip } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import {
  RelatedSkill,
  SearchService,
} from '@app/search/services/search.service';
import { TagsApi } from '@app/tags/tag-api.model';
import { SearchTrackerService } from '@app/search/services/search-tracker.service';
import { Opportunity } from '@app/opportunities/opportunities-api.model';
import { OpportunitiesMatchViewModel } from '@app/search/components/search-opportunities/search-opportunities.component';
import { TranslateService } from '@ngx-translate/core';
import { TagRatingService } from '@app/tags/services/tag-rating.service';
import { SkillsMatchViewModel } from '@app/search/components/search-skills/search-skills.component';
import { PeopleMatchViewModel } from '@app/search/components/search-people/search-people.component';
import { ProximitySearchResults } from '@app/user/user-api.model';
import { GroupDetail } from '@app/groups/group-api';
import { GroupsMatchViewModel } from '@app/search/components/search-groups/search-groups.component';
import { SearchFlagsService } from '@app/search/services/search-flags.service';
import { SearchFiltersStateService } from '../../search-filters/search-filters-state.service';
import { coerce, PAGINATION_LIMIT } from './utils';
import {
  SearchLearningOptions,
  SearchLearningResponse,
} from './models/search.model';
import { SearchRelatedItemsResponse } from './models/search-related.model';
import {
  GroupFindModel,
  OpportunitiesResults,
  PeopleModel,
  TagDetailResults,
} from '@app/search';
import {
  FeaturedItemsMatchViewModel,
  MarketplaceFeaturedItems,
  SearchMarketplaceService,
} from '@app/search/components/search-view/search-reactive-store';
import { LearningResourceViewModel } from '@app/inputs/models/learning-resource.view-model';

/**
 * Swallow errors for sidebar and other requests to prevent page load issues
 */
export const handleError = <T>(source$: Observable<T>, emptyResponse?: T) => {
  emptyResponse ||= SearchDataService.EMPTY_RESPONSE as unknown as T;

  const onError = (error: Error): Observable<T> => {
    console.error(error);
    return of(emptyResponse);
  };
  return source$.pipe(catchError(onError));
};

/**
 * A Proxy to the SearchDataService for REST API calls.
 * Uses a proxy to leverage existing functionality in existing infrastructure.
 */
@Injectable()
export class SearchDataService {
  public static EMPTY_RESPONSE = { items: [], total: 0 };

  private i18n: Record<string, string>;
  private suggestedTermCache: string;

  constructor(
    private searchService: SearchService,
    private searchTrackerService: SearchTrackerService,
    private translateService: TranslateService,
    private tagRatingService: TagRatingService,
    private searchFlagsService: SearchFlagsService,
    private searchFiltersStateService: SearchFiltersStateService,
    private opportunityPermissionsService: OpportunityPermissionsService,
    private searchMarketplaceService: SearchMarketplaceService
  ) {
    this.i18n = this.translateService.instant([
      'dgTagRating_RemoveFromProfileTooltip',
      'dgTagRating_AddToProfileTooltip',
      'Core_UnFollow',
      'Core_Follow',
    ]);
  }

  /**
   * Search all 'LearningResources'
   */
  public searchLearnings(
    learningOptions: SearchLearningOptions,
    // Most search actions (filter/sort/paginate) don't need spellcheck to run so...
    spellcheckSearchTerm = false
  ): Observable<SearchLearningResponse> {
    const { term, options } = learningOptions;

    return this.loadLearnings(learningOptions)
      .pipe(
        switchMap((learnings) =>
          zip(
            of(learnings),
            this.getSuggestedTerm(term, learnings.total, spellcheckSearchTerm)
          )
        ),
        switchMap(([learnings, suggestedTerm]) => {
          const { results } = learnings;
          const shouldSearchAgain = results.length === 0 && !!suggestedTerm;

          // If we have no results AND a possible typo, search again w/suggested term
          return shouldSearchAgain
            ? this.loadLearnings({
                ...learningOptions,
                term: suggestedTerm,
              }).pipe(
                map((learnings) => ({
                  ...learnings,
                  term: suggestedTerm,
                  suggestedTerm,
                  previousTerm: term,
                  typoAutoSearched: true,
                }))
              )
            : of({
                ...learnings,
                suggestedTerm,
                previousTerm: undefined,
                typoAutoSearched: false,
              });
        })
      )
      .pipe(
        map(
          ({
            term,
            results,
            filters,
            total,
            suggestedTerm,
            previousTerm,
            typoAutoSearched,
          }) => {
            const lastPage = Math.ceil(total / PAGINATION_LIMIT);
            return {
              term,
              results,
              filters,
              suggestedTerm,
              previousTerm,
              typoAutoSearched,
              pagination: {
                total,
                perPage: PAGINATION_LIMIT,
                lastPage,
                currentPage: options.pageNum || 1,
              },
            };
          }
        )
      );
  }

  /**
   * Format related skills response for use in SearchResultsComponent
   */
  public loadRelatedSkills(
    term: string
  ): Observable<SearchRelatedItemsResponse> {
    const request$ = this.searchService.getRelatedTerms(term);
    const transformResponse = (items: RelatedSkill[]) => {
      const total = items?.length || 0;
      return { items, total };
    };

    return term
      ? handleError(request$.pipe(map(transformResponse)))
      : of(SearchDataService.EMPTY_RESPONSE);
  }

  /**
   * Format opportunities response for use in SearchResultsComponent
   */
  public loadOpportunities(
    term: string,
    orgId: number
  ): Observable<SearchRelatedItemsResponse> {
    const request$ = this.searchService.opportunitiesSearch(term, orgId);
    const transformResponse = (response: OpportunitiesResults) => {
      const items = response?.items || [];
      const total = response?.totalCount || 0;
      return {
        items: items.map((model: Opportunity) =>
          this.transformOpportunitiesModel(model, total)
        ),
        total,
      };
    };

    return orgId && this.opportunityPermissionsService.canViewOpportunities()
      ? handleError(request$.pipe(map(transformResponse)))
      : of(SearchDataService.EMPTY_RESPONSE);
  }

  /**
   * Format skills response for use in SearchResultsComponent
   */
  public loadSkills(
    term: string,
    orgId: number
  ): Observable<SearchRelatedItemsResponse> {
    const request$ = this.searchService.skillsSearch(term);
    const transformResponse = (response: TagDetailResults) => {
      const items = response?.tagDetails || [];
      const total = response?.total || 0;
      return {
        items: items.map((model: TagsApi.TagDetails) =>
          this.transformSkillsModel(model, total)
        ),
        total,
      };
    };

    return orgId
      ? handleError(request$.pipe(map(transformResponse)))
      : of(SearchDataService.EMPTY_RESPONSE);
  }

  /**
   * Format users response for use in SearchResultsComponent
   */
  public loadPeople(
    term: string,
    orgId: number
  ): Observable<SearchRelatedItemsResponse> {
    const request$ = this.searchService.peopleSearch(term, orgId);
    const tranformResponse = (response: PeopleModel) => {
      const items = response?.profiles || [];
      const total = response?.total || 0;
      return {
        items: items.map((model: ProximitySearchResults) =>
          this.transformPeopleModel(model, total)
        ),
        total,
      };
    };

    return handleError(request$.pipe(map(tranformResponse)));
  }

  /**
   * Format groups response for use in SearchResultsComponent
   */
  public loadGroups(
    term: string,
    orgId: number
  ): Observable<SearchRelatedItemsResponse> {
    const request$ = this.searchService.groupsSearch(term, orgId);
    const transformResponse = (response: GroupFindModel) => {
      const items = response?.groups || [];
      const total = response?.total || 0;
      return {
        items: items.map((model: GroupDetail) =>
          this.transformGroupModel(model, total)
        ),
        total,
      };
    };
    return orgId
      ? handleError(request$.pipe(map(transformResponse)))
      : of(SearchDataService.EMPTY_RESPONSE);
  }

  public loadEndorsedLearnings(
    { term, orgId, options }: SearchLearningOptions,
    count: number,
    skip: number
  ) {
    const { appliedFacets, boostRecent, boostPopular } = options;
    const ignorePreferredLanguage =
      this.searchFiltersStateService.ignorePreferredLanguage;

    return this.searchService.search(
      coerce<string>(term, ''),
      appliedFacets,
      count,
      skip,
      orgId,
      coerce<boolean>(boostRecent, false),
      coerce<boolean>(boostPopular, false),
      ignorePreferredLanguage,
      false,
      [],
      false
    );
  }

  public loadFeaturedItems() {
    const request$ = this.searchMarketplaceService.searchFeaturedItems();
    const transformResponse = (
      response: MarketplaceFeaturedItems
    ): SearchRelatedItemsResponse => {
      const items = response?.items || [];
      const total = response?.total || 0;
      return {
        items: items.map((model: LearningResourceViewModel) =>
          this.transformFeaturedItemsModel(model)
        ),
        total,
      };
    };
    return handleError(request$.pipe(map(transformResponse)));
  }

  private loadLearnings({ term, orgId, options }: SearchLearningOptions) {
    const {
      appliedFacets,
      boostRecent,
      boostPopular,
      pageNum,
      isMarketplace,
      persistFilter,
    } = options;
    const skip = (pageNum - 1) * PAGINATION_LIMIT;
    const ignorePreferredLanguage =
      this.searchFiltersStateService.ignorePreferredLanguage;
    if (isMarketplace) {
      return this.searchMarketplaceService.searchMarketplace(
        term,
        appliedFacets,
        PAGINATION_LIMIT,
        skip,
        pageNum
      );
    }

    return this.searchService.search(
      coerce<string>(term, ''),
      appliedFacets,
      PAGINATION_LIMIT,
      skip,
      orgId,
      coerce<boolean>(boostRecent, false),
      coerce<boolean>(boostPopular, false),
      ignorePreferredLanguage,
      false,
      [],
      persistFilter || false
    );
  }

  private getSuggestedTerm(
    term: string,
    totalResults: number,
    spellcheckSearchTerm: boolean
  ): Observable<string> {
    // If we have more than 9 results, clear the cache
    // and return an empty observable
    if (totalResults > 9) {
      return of((this.suggestedTermCache = undefined));
    }

    return spellcheckSearchTerm
      ? // The search term has changed and the user did NOT select the
        // previously suggested term, so we need to spellcheck
        this.spellcheck(term).pipe(
          tap(
            (response) =>
              // Cache the suggested term so it persists in the UI and return it
              (this.suggestedTermCache = response)
          )
        )
      : // Either the search term hasn't changed OR the user selected
        // the previously suggested term
        of(
          term === this.suggestedTermCache
            ? // suggested term selected, so clear cache...
              (this.suggestedTermCache = undefined)
            : // otherwise persist suggested term...
              this.suggestedTermCache
        );
  }

  /**
   * Check search term spelling
   * TODO: delete this once we handle suggested term on back end
   */
  private spellcheck(term: string): Observable<string> {
    // TODO: delete this w/https://degreedjira.atlassian.net/browse/PD-77322
    return this.searchFlagsService.showTypoSuggestions
      ? this.searchService.spellcheck(term)
      : of(null);
  }

  /**
   * Format the opportunities model for use in SearchOpportunitiesComponent & ListItemComponent
   */
  private transformOpportunitiesModel(
    model: Opportunity,
    total: number
  ): OpportunitiesMatchViewModel {
    const { title, type, locationName, opportunityId } = model;
    const description = locationName ? `${type},  ${locationName}` : type;
    const linkUrl = `/opportunities/${opportunityId}`;
    const defaultAction = () =>
      this.searchTrackerService.opportunitiesOpened(model, total);
    return {
      model,
      title,
      description,
      linkUrl,
      defaultAction,
    };
  }

  /**
   * Format the skills model for use in SearchSkillsComponent & ListItemComponent
   */
  private transformSkillsModel(
    model: TagsApi.TagDetails,
    total: number
  ): SkillsMatchViewModel {
    const { title, tagId, isFollowing } = model;
    const linkUrl = `/resource/tag?id=${tagId}`;
    const explainText = isFollowing
      ? this.i18n.dgTagRating_RemoveFromProfileTooltip
      : this.i18n.dgTagRating_AddToProfileTooltip;
    const defaultAction = (event: Event) => {
      event.stopPropagation();
      event.preventDefault();
      this.searchTrackerService.skillRatingsListOpened(model, total);
      this.tagRatingService.openSkillModal(event, model, false);
    };
    return {
      model,
      title,
      linkUrl,
      explainText,
      defaultAction,
    };
  }

  /**
   * Format the people model for use in SearchPeopleComponent & ListItemComponent
   */
  private transformPeopleModel(
    model: ProximitySearchResults,
    total: number
  ): PeopleMatchViewModel {
    const { name: title, tags: description, vanityUrl, isFollowing } = model;
    const linkUrl = `/${vanityUrl}`;
    const explainText = isFollowing
      ? this.i18n.Core_UnFollow
      : this.i18n.Core_Follow;
    const defaultAction = () =>
      this.searchTrackerService.userOpened(model, total);
    return {
      model,
      title,
      description,
      linkUrl,
      explainText,
      defaultAction,
    };
  }

  /**
   * Format the group model for use in SearchGroupsComponent & ListItemComponent
   */
  private transformGroupModel(
    model: GroupDetail,
    total: number
  ): GroupsMatchViewModel {
    const { name: title, groupId } = model;
    const linkUrl = `/groups/${groupId}`;
    const defaultAction = () =>
      this.searchTrackerService.groupsOpened(model, total);
    return {
      model,
      title,
      linkUrl,
      defaultAction,
    };
  }

  /**
   * Format the opportunities model for use in SearchOpportunitiesComponent & ListItemComponent
   */
  private transformFeaturedItemsModel(
    model: LearningResourceViewModel
  ): FeaturedItemsMatchViewModel {
    const { resourceId, title } = model;
    // we haven't sorted out the tracking requirements for this yet
    const defaultAction = () => {};
    return {
      defaultAction,
      id: resourceId,
      model,
      title,
    };
  }
}
